import React from 'react';
import { RefreshCw, Bookmark, Trash2, User, Sparkles, ExternalLink, Share2 } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';

const SavedView = ({ savedArticles, fetchSavedArticles, deleteSavedArticle, renderAISummary }) => {
  const { language } = useLanguage();

  const handleShare = async (item) => {
    const shareUrl = item.article_url || 'https://www.sciencemonitor.se';
    const cleanSummary = typeof item.ai_summary === 'string' 
      ? item.ai_summary.slice(0, 160) 
      : '';

    const shareData = {
      title: item.article_title || 'Science Monitor',
      text: `${item.article_title}\n\n${cleanSummary}...\n\nLäs mer via Science Monitor:`,
      url: shareUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // Avbröts av användaren
      }
    } else {
      await navigator.clipboard.writeText(`${item.article_title} - ${shareUrl}`);
      alert(language === 'sv' ? 'Länk kopierad till urklipp!' : 'Link copied to clipboard!');
    }
  };

  return (
    <div className="space-y-8 md:space-y-10 animate-in fade-in max-w-6xl mx-auto px-4 md:px-0">
      <div className="flex items-center justify-between border-b border-slate-200 pb-4 md:pb-6 mt-8 md:mt-12">
        <div>
          <h2 className="text-2xl md:text-3xl font-serif text-slate-800 italic tracking-tight">{t(language, 'saved_title')}</h2>
          <p className="text-slate-500 text-xs md:text-sm mt-1">{t(language, 'saved_subtitle')}</p>
        </div>
        <button onClick={fetchSavedArticles} className="p-3 bg-white border border-slate-200 shadow-sm rounded-xl hover:bg-slate-50 text-slate-600 active:scale-95 transition-all" title={t(language, 'saved_refresh')}><RefreshCw size={18}/></button>
      </div>

      {savedArticles.length === 0 ? (
        <div className="text-center py-20 bg-slate-50 rounded-3xl border border-slate-200 border-dashed m-2">
          <Bookmark size={48} className="mx-auto text-slate-300 mb-4"/>
          <p className="text-slate-500 font-medium">{t(language, 'saved_empty')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mt-2">
          {savedArticles.map(item => (
            <div key={item.id} className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden text-left transition-transform hover:scale-[1.01] flex flex-col">
              <div className="p-5 md:p-8 border-b border-slate-50 shrink-0">
                <div className="flex justify-between items-start mb-4">
                  <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest bg-blue-50 px-3 py-1 rounded-full">{item.topic}</span>
                  <button onClick={() => deleteSavedArticle(item.id)} className="text-slate-300 hover:text-red-600 p-2 rounded-full transition-all" title={t(language, 'saved_delete')}><Trash2 size={20}/></button>
                </div>
                <h3 className="text-xl md:text-2xl font-serif mb-3 leading-tight text-slate-900">{item.article_title}</h3>
                <div className="space-y-2 mb-4">
                  <p className="text-xs md:text-sm text-slate-600 font-medium italic flex items-start gap-2">
                    <User size={14} className="shrink-0 mt-0.5"/> 
                    <span className="leading-relaxed">{item.authors || t(language, 'saved_unknown_author')}</span>
                  </p>
                  <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-tighter font-sans flex items-center gap-2 truncate">
                    {item.venue || t(language, 'saved_unknown_journal')} • {item.article_date}
                  </p>
                </div>
              </div>
              <div className="p-5 md:p-8 bg-slate-50/50 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-[10px] font-bold text-indigo-500 uppercase tracking-widest mb-4"><Sparkles size={14}/> {t(language, 'saved_ai_summary')}</div>
                <div className="text-slate-700 leading-relaxed whitespace-pre-line text-base md:text-lg font-serif flex-1">
                  {renderAISummary(item.ai_summary, language)}
                </div>
                <div className="pt-4 mt-4 border-t border-slate-100 flex justify-between items-center shrink-0">
                  <a href={item.article_url} target="_blank" rel="noreferrer" className="text-[11px] font-bold text-blue-600 hover:text-blue-800 uppercase flex items-center gap-1">
                    {t(language, 'saved_read_original')} <ExternalLink size={12}/>
                  </a>
                  <button
                    onClick={() => handleShare(item)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 transition-colors active:scale-95"
                    title={language === 'sv' ? 'Dela sammanfattning' : 'Share summary'}
                  >
                    <Share2 size={14} />
                    <span>{language === 'sv' ? 'Dela' : 'Share'}</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedView;